import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AHBoolean from "_shared/booleans/boolean";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { Masked, MaskedTeam } from "work/_shared/masked-fields";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import SwitchRedline from "work/proposals/redlining/field-redlines/_views/switch-redline";
import TeamRedlineSelection from "work/proposals/redlining/field-redlines/_views/team-redline-selection";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  getErrorLoadingTeamTemplatesByQuery,
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
  getTeamTemplatesQueryByInterface,
  populateTeamTemplatesByQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "20rem",
}));
const TemplateContainer = styled(Grid)(({ theme }) => ({
  height: "25rem",
  overflow: "auto",
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));
const TemplateList = styled(List)(({ theme }) => ({
  marginRight: theme.spacing(2),
  maxHeight: "25rem",
  minWidth: "20rem",
  overflowY: "auto",
}));
const TeamGrid = styled(Grid)(() => ({
  height: "25rem",
  overflow: "auto",
}));
const TemplateLoader = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  margin: theme.spacing(2),
}));
const RedlineTeamSelect = styled(TeamRedlineSelection)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));
const HeadControls = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(2),
}));
const Change = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  }),
);
const IssueContainer = styled("div")<{ active?: boolean }>(({ theme, active }) => ({
  animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
  margin: active ? theme.spacing(1) : "initial",
  marginLeft: 0,
  marginTop: active ? 0 : theme.spacing(1),
  padding: active ? theme.spacing(1) : "initial",
  borderRadius: theme.shape.borderRadius,
}));

type TeamTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  teamLeader: EntityMember | undefined;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, title?: string) => void;
  onTeamMemberQuitProposal: (memberId: Guid) => void;
};

const TeamTab = (props: Readonly<TeamTabProps>) => {
  const INTERFACE_NAME = "ActiveProposalTeamTab";

  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    teamLeader,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,
    onTeamMemberQuitProposal,
  } = props;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const selectedTemplateIds = MaskedTeam.isPresentNotMasked(proposalRedline.vendorTeam)
    ? proposalRedline.vendorTeam.templateIds
    : [];
  const filterParameters = new TeamTemplateFilterParameters({ creatorEntityId: session.user!.legalEntityId });
  const orderParameters = new TeamTemplateOrderParameters({
    orderBy: TeamTemplateOrderField.Name,
    orderDir: OrderDirection.Descending,
  });
  const defaultTemplateQuery: TeamTemplateQuery = new TeamTemplateQuery({
    filterParameters,
    orderParameters,
    paginationParameters: PaginationParameters.DEFAULT,
  });
  const templateQuery = useSelector(getTeamTemplatesQueryByInterface(INTERFACE_NAME)) ?? defaultTemplateQuery;
  const teamTemplates = useSelector(getTeamTemplatesByQuery(templateQuery));
  const isLoadingTeamTemplates = useSelector(getIsLoadingTeamTemplatesByQuery(templateQuery));
  const errorLoadingTeamTemplates = useSelector(getErrorLoadingTeamTemplatesByQuery(templateQuery));

  const individualUserIds: Set<string> = useMemo(() => {
    const userIds: Set<string> = new Set();
    teamTemplates?.data.forEach((template) => {
      template.members.forEach((member) => userIds.add(member.userId.value));
      userIds.add(template.leader.userId.value);
    });
    return userIds;
  }, [teamTemplates]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: individualUserIds } : skipToken
  );

  const individualsPool: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<TeamTemplate>>(new Set());

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Team,
          ProposalField.TeamRestriction,
          ProposalField.TeamLeader,
          ProposalField.TeamMember(issue.field.id ?? Guid.EMPTY),
        ].includes(issue.field),
      ),
    [props.issues?.entries],
  );
  const isLargeDownDisplay = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (teamTemplates === undefined && !isLoadingTeamTemplates && !errorLoadingTeamTemplates) {
      dispatch(
        populateTeamTemplatesByQuery({
          session,
          query: templateQuery,
        }),
      );
    }
  }, [dispatch, errorLoadingTeamTemplates, isLoadingTeamTemplates, session, teamTemplates, templateQuery]);

  async function handleTemplateSelectionToggled(template: TeamTemplate): Promise<void> {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      throw new Error("You do not have permission to edit the team members.");
    }
    try {
      if (!template.id) {
        console.error(new Error("No template selected"));
        return;
      }
      const selectedTemplateIds = proposalRedline.vendorTeam.templateIds;
      let updatedSelectedTemplateIds = [...selectedTemplateIds];

      let newProposalRedline = proposalRedline;
      let newTeamMembersRedline: FieldRedlineArray<EntityMember> = proposalRedline.vendorTeam;
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        newTeamMembersRedline = newTeamMembersRedline.updateTemplateIds(updatedSelectedTemplateIds);
      } else {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        updatedSelectedTemplateIds.push(template.id);
        newTeamMembersRedline = newTeamMembersRedline.updateTemplateIds(updatedSelectedTemplateIds);
        template.members.forEach((member) => {
          if (!getIsMemberAlreadySelected(member)) {
            newTeamMembersRedline = newTeamMembersRedline.addEntry(member);
          }
        });
      }

      newProposalRedline = proposalRedline.updateTeamMembersRedline(newTeamMembersRedline);
      onProposalRedlineChange(newProposalRedline, null);
      onCommentsClicked(ProposalField.Team, "Team");
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to apply template. Please try again", {
        variant: "error",
      });
    }
  }

  function handleToggleTeamMemberSelection(member: EntityMember) {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      throw new Error("You do not have permission to edit the team members.");
    }

    if (getIsMemberAlreadySelected(member)) {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(
          proposalRedline.vendorTeam.removeEntryByFieldId(member?.id ?? undefined),
        ),
        null,
      );
    } else {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(proposalRedline.vendorTeam.addEntry(member)),
        null,
      );
    }
    onCommentsClicked(ProposalField.Team, "Team");
  }

  function getIsMemberAlreadySelected(member: EntityMember): boolean {
    if (Masked.isMasked(proposalRedline.vendorTeam)) return false;
    return (
      (teamLeader?.isEqualTo(member) ?? false) ||
      proposalRedline.vendorTeam.redlines.some((individualRedline) =>
        individualRedline.currentEntry ? individualRedline.currentEntry.isEqualTo(member) : false,
      )
    );
  }

  function handleTemplateExpanded(template: TeamTemplate) {
    let updatedExpandedTemplates = new Set(expandedTemplates);
    if (updatedExpandedTemplates.has(template)) {
      updatedExpandedTemplates.delete(template);
    } else {
      updatedExpandedTemplates.add(template);
    }
    setExpandedTemplates(updatedExpandedTemplates);
  }

  function renderTemplates() {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      return "You do not have permission to view teams.";
    }
    return (
      <TemplateList dense subheader={<ListSubheader>Templates</ListSubheader>}>
        {teamTemplates?.data.length === 0 && (
          <>
            {isLoadingTeamTemplates ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {teamTemplates?.data.map((template: TeamTemplate) => {
          const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));

          if (Masked.isMasked(proposalRedline.vendorTeam)) {
            throw new Error("You do not have permission to edit the team members.");
          }

          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip title={`${expandedTemplates.has(template) ? "Hide" : "Show"} Members`}>
                    <span>
                      <IconButton
                        edge="end"
                        disabled={!proposalRedline.vendorTeam?.isResolved}
                        onClick={() => handleTemplateExpanded(template)}
                        size="medium"
                      >
                        {expandedTemplates.has(template) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip title={isSelected ? "Deselect Template" : "Select Template"}>
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={
                          !isSelected &&
                          (template.userIsReferenced(teamLeader?.userId) ||
                            proposalRedline.vendorTeam.redlines
                              .map((memberRedline) => memberRedline.field.id)
                              .some((memberId) => template.userIsReferenced(memberId ?? undefined)))
                        }
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleTemplateSelectionToggled(template)}
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText>{template.name.value}</ListItemText>
              </ListItem>
              <Collapse in={expandedTemplates.has(template)} timeout="auto" sx={{ backgroundColor: "#FAFAFA" }}>
                <Divider />
                <List disablePadding dense subheader={<ListSubheader>Leader</ListSubheader>}>
                  {renderTemplateLeader(template)}
                </List>
                <List disablePadding dense subheader={<ListSubheader>Members</ListSubheader>}>
                  {renderTemplateMembers(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateLeader(template: TeamTemplate) {
    const leaderIndividual = individualsPool.find((ind) => ind.userId.isEqualTo(template.leader.userId));
    if (!leaderIndividual) {
      return null;
    }
    return (
      <ListItem
        key="teamLeader"
        dense
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            disabled
            size="small"
            checked={Boolean(teamLeader?.userId?.isEqualTo(template?.leader?.userId))}
            tabIndex={-1}
            disableRipple
          />
        }
      >
        <ListItemText>{individualQuery.isLoading ? "Loading..." : leaderIndividual.fullName}</ListItemText>
      </ListItem>
    );
  }

  function renderTemplateMembers(template: TeamTemplate) {
    if (Masked.isMasked(proposalRedline.vendorTeam)) {
      return "You do not have permission to view team members.";
    }

    return template?.members?.map((member) => {
      const individual = individualsPool.find((ind) => ind.userId.isEqualTo(member.userId));
      if (!individual) {
        return null;
      }
      if (Masked.isMasked(proposalRedline.vendorTeam)) {
        throw new Error("You do not have permission to edit the team members.");
      }

      return (
        <ListItem
          key={member.userId?.value}
          dense
          onClick={() => handleToggleTeamMemberSelection(member)}
          secondaryAction={
            <Checkbox
              edge="end"
              color="primary"
              size="small"
              checked={proposalRedline.vendorTeam?.redlines.some((m) =>
                m.currentEntry?.userId?.isEqualTo(member.userId),
              )}
              tabIndex={-1}
              disableRipple
              onClick={() => handleToggleTeamMemberSelection(member)}
            />
          }
        >
          <ListItemText
            style={{ whiteSpace: "pre-line" }}
            primary={individualQuery.isLoading ? "Loading..." : individual.fullName}
          />
        </ListItem>
      );
    });
  }

  function handleTeamRestrictedChanged(
    newRedline: FieldRedline<AHBoolean>,
    traversalFieldOverride?: ProposalField | null,
  ) {
    try {
      onProposalRedlineChange(proposalRedline.updateTeamRestrictedRedline(newRedline), traversalFieldOverride);
      onCommentsClicked(ProposalField.Team, "Team");
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error updating vendor-team restriction", { variant: "error" });
    }
  }

  if (Masked.isMasked(proposalRedline.vendorTeam) || Masked.isMasked(proposalRedline.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer>
            {issues?.map((issue: ProposalIssue) => (
              <Typography
                key={issue.field?.cacheKey ?? Guid.generate().value}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <Change active={activeReviewField?.isEqualTo(ProposalField.TeamRestriction) ?? false}>
          <SwitchRedline
            boolRedline={proposalRedline?.teamRestricted}
            disabled={disableEditing}
            promptTitleText="Restrict team?"
            promptMessageText="Restrict team members from freely leaving team without triggering a renegotiation?"
            okButtonText="Restrict Team"
            unsetLabel="Team Not Restricted"
            setLabel="Team Restricted"
            onBoolRedlineChange={(newRedline, traversalFieldOverride) => {
              try {
                handleTeamRestrictedChanged(newRedline, traversalFieldOverride);
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Error updating vendor-team restriction", {
                  variant: "error",
                });
              }
            }}
          />
        </Change>
      </HeadControls>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <TemplateContainer>
              {renderTemplates()}
              {isLargeDownDisplay && <Divider />}
            </TemplateContainer>
            {!isLargeDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <TeamGrid size="grow">
          <RedlineTeamSelect
            activeReviewField={activeReviewField}
            commentThreads={commentThreads}
            disableEditing={disableEditing}
            teamLeader={teamLeader}
            teamMembersRedline={proposalRedline.vendorTeam}
            onCommentsClicked={onCommentsClicked}
            onTeamMembersRedlineChange={(
              newTeamRedline: FieldRedlineArray<EntityMember>,
              traversalFieldOverride?: ProposalField | null,
            ) => {
              try {
                onProposalRedlineChange(
                  proposalRedline.updateTeamMembersRedline(newTeamRedline),
                  traversalFieldOverride,
                );
                onCommentsClicked(ProposalField.Team, "Team");
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Error updating vendor-team members", {
                  variant: "error",
                });
              }
            }}
            onMemberQuitTeam={(memberId: Guid) => {
              try {
                onTeamMemberQuitProposal(memberId);
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Error quitting vendor-team", { variant: "error" });
              }
            }}
            onMemberClicked={(memberId: Guid) => {
              onTraverseToNewField?.(ProposalField.TeamMember(memberId));
            }}
          />
        </TeamGrid>
      </ListContainer>
    </>
  );
};

export default TeamTab;
